<script>
  import { createEventDispatcher, onDestroy, tick } from 'svelte';
  import { initiateSigning, newIdempotencyKey, getSigningProfile } from './agreementApi.js';

  export let requirement = null;
  export let open = false;
  export let returnFocus = null;
  const dispatch = createEventDispatcher();
  let dialog;
  let summary;
  let firstInput;
  let submitting = false;
  let fields = emptyFields();
  let errors = {};
  let submitError = '';
  let idempotencyKey = '';
  let wasOpen = false;
  let profileRequest;
  let editedFields = new Set();

  function emptyFields() { return { legal_name: '', legal_surname: '', country: 'LT', email: '', personal_code: '' }; }
  function clearSensitiveState() {
    profileRequest?.abort(); profileRequest = null; editedFields = new Set();
    fields = emptyFields(); errors = {}; submitError = ''; idempotencyKey = '';
  }

  $: if (open && !wasOpen) { wasOpen = true; openDialog(); }
  $: if (!open && wasOpen) { wasOpen = false; closeNativeDialog(); }

  async function openDialog() {
    await tick();
    if (dialog && !dialog.open) dialog.showModal();
    await tick();
    firstInput?.focus();
    if (!open || !wasOpen) return;
    const controller = new AbortController();
    profileRequest = controller;
    try {
      const profile = await getSigningProfile(controller.signal);
      if (controller.signal.aborted || !open || submitting) return;
      const defaults = { legal_name: profile?.first_name, legal_surname: profile?.last_name, email: profile?.email };
      for (const [key, value] of Object.entries(defaults)) {
        if (!editedFields.has(key) && typeof value === 'string') fields[key] = value;
      }
      fields = fields;
    } catch (_) { /* Profile suggestions are optional; manual entry still works. */ }
  }

  function closeNativeDialog() {
    if (dialog?.open) dialog.close();
    clearSensitiveState();
    tick().then(() => returnFocus?.focus());
  }

  function cancel() {
    if (submitting) return;
    clearSensitiveState();
    dispatch('close');
    tick().then(() => returnFocus?.focus());
  }

  function validate() {
    const next = {};
    for (const [key, value] of Object.entries(fields)) if (!String(value).trim()) next[key] = 'Privalomas laukas.';
    if (fields.email && !/^\S+@\S+\.\S+$/.test(fields.email.trim())) next.email = 'Patikrinkite el. pašto adresą.';
    errors = next;
    return Object.keys(next).length === 0;
  }

  async function submit() {
    if (submitting) return;
    submitError = '';
    if (!validate()) { await tick(); summary?.focus(); return; }
    submitting = true;
    if (!idempotencyKey) idempotencyKey = newIdempotencyKey();
    try {
      const result = await initiateSigning(requirement.public_id || requirement.id, {
        legal_name: fields.legal_name.trim(), legal_surname: fields.legal_surname.trim(),
        country: fields.country.trim().toUpperCase(), email: fields.email.trim(), personal_code: fields.personal_code
      }, idempotencyKey);
      const mode = result?.mode;
      if (['provider_failed', 'declined', 'expired'].includes(result?.status)) {
        throw { status: 400, code: 'signing_start_failed', retryAllowed: false };
      }
      const redirectUrl = result?.redirect_url || result?.redirect_uri;
      if (mode === 'gateway_iframe') {
        if (typeof result?.launch_url !== 'string' || !result.launch_url) throw new Error('invalid_gateway_iframe');
        clearSensitiveState();
        dispatch('submitted', { mode, result });
        dispatch('close');
        return;
      }
      if (mode === 'redirect') {
        let parsed;
        if (typeof redirectUrl !== 'string' || !redirectUrl.trim()) throw new Error('invalid_redirect');
        try { parsed = new URL(redirectUrl, window.location.origin); } catch (_) { throw new Error('invalid_redirect'); }
        const isSecureProviderRedirect = parsed.protocol === 'https:';
        const isSameOriginLocalRedirect = parsed.origin === window.location.origin
          && (parsed.protocol === 'http:' || parsed.protocol === 'https:')
          && parsed.pathname === '/studentplayground/api/agreements/mock/sign/';
        if (!isSecureProviderRedirect && !isSameOriginLocalRedirect) throw new Error('invalid_redirect');
        clearSensitiveState();
        dispatch('submitted', { mode, result });
        window.location.assign(parsed.href);
        return;
      }
      if (mode === 'email_invitation' || mode === 'invitation_sent' || result?.status === 'invitation_sent') {
        clearSensitiveState(); dispatch('submitted', { mode: 'invitation_sent', result }); dispatch('close'); return;
      }
      clearSensitiveState(); dispatch('submitted', { mode: 'reconcile', result }); dispatch('close');
    } catch (error) {
      if (error?.retryAllowed === true) idempotencyKey = '';
      if (error?.code === 'signing_access_denied') submitError = 'Dokobit prieigos raktas atmestas. Tai serverio konfigūracijos problema, ne pasirašančio asmens duomenys.';
      else if (error?.code === 'signing_outcome_unknown') submitError = 'Nepavyko patvirtinti, ar pasirašymas pradėtas. Patikrinkite būseną prieš pradėdami naują bandymą.';
      else if (error?.code === 'signing_start_failed') submitError = 'Dokobit pasirašymo pradėti nepavyko. Bandykite vėliau arba kreipkitės į administratorių.';
      else if (error?.message === 'invalid_redirect') submitError = 'Gautas nesaugus pasirašymo adresas. Bandykite atnaujinti būseną.';
      else if (!error?.status || error.status >= 500 || error.status === 429) submitError = 'Nepavyko patvirtinti, ar pasirašymas pradėtas. Patikrinkite būseną ir bandykite dar kartą.';
      else submitError = 'Pasirašymo pradėti nepavyko. Patikrinkite duomenis.';
      fields.personal_code = '';
      fields = fields;
      await tick(); summary?.focus();
    } finally { submitting = false; }
  }

  function trapFocus(event) {
    if (event.key === 'Escape') { event.preventDefault(); cancel(); return; }
    if (event.key !== 'Tab') return;
    const focusable = dialog.querySelectorAll('button:not([disabled]),input:not([disabled]),a[href]');
    if (!focusable.length) return;
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  onDestroy(clearSensitiveState);
</script>

<dialog bind:this={dialog} class="signing-dialog" on:cancel|preventDefault={cancel} on:keydown={trapFocus} on:close={() => open && cancel()}>
  <form method="dialog" on:submit|preventDefault={submit} autocomplete="off">
    {#if requirement?.family}<p>Šeimos sutartis: {requirement.family.name}. Pakanka vieno parašo visai šeimai.<br>{requirement.covered_students?.map(student => `${student.first_name} ${student.last_name}`).join(', ') || ''}</p>{/if}
    <button type="button" class="dialog-close" aria-label="Uždaryti pasirašymo langą" disabled={submitting} on:click={cancel}>×</button>
    <h2>Pasirašyti sutartį</h2>
    <p><strong>{requirement?.title || requirement?.version_title || 'Paslaugų teikimo sutartis'}</strong>{requirement?.version_number ? ` · versija ${requirement.version_number}` : ''}</p>
    <p>Dokobit patvirtins pasirašančio asmens tapatybę. Įveskite savo – paskyrą valdančio tėvo, globėjo ar mokinio – teisinius duomenis.</p>
    {#if submitError || Object.keys(errors).length}
      <div class="error-summary" role="alert" tabindex="-1" bind:this={summary}>{submitError || 'Patikrinkite pažymėtus laukus.'}</div>
    {/if}
    <label for="agreement-name">Vardas</label><input bind:this={firstInput} id="agreement-name" bind:value={fields.legal_name} on:input={() => editedFields.add('legal_name')} disabled={submitting} aria-invalid={!!errors.legal_name} aria-describedby={errors.legal_name ? 'agreement-name-error' : undefined} autocomplete="off">{#if errors.legal_name}<small id="agreement-name-error">{errors.legal_name}</small>{/if}
    <label for="agreement-surname">Pavardė</label><input id="agreement-surname" bind:value={fields.legal_surname} on:input={() => editedFields.add('legal_surname')} disabled={submitting} aria-invalid={!!errors.legal_surname} autocomplete="off">{#if errors.legal_surname}<small>{errors.legal_surname}</small>{/if}
    <label for="agreement-country">Šalis</label><input id="agreement-country" bind:value={fields.country} maxlength="2" disabled={submitting} aria-invalid={!!errors.country} autocomplete="off">{#if errors.country}<small>{errors.country}</small>{/if}
    <label for="agreement-email">El. paštas</label><input id="agreement-email" type="email" bind:value={fields.email} on:input={() => editedFields.add('email')} disabled={submitting} aria-invalid={!!errors.email} autocomplete="off">{#if errors.email}<small>{errors.email}</small>{/if}
    <label for="agreement-code">Asmens kodas</label><input id="agreement-code" type="text" bind:value={fields.personal_code} disabled={submitting} aria-invalid={!!errors.personal_code} autocomplete="off" autocapitalize="none" spellcheck="false">{#if errors.personal_code}<small>{errors.personal_code}</small>{/if}
    <p class="privacy">Asmens kodas perduodamas Dokobit tapatybei patvirtinti. Tutory jo neišsaugo.</p>
    <div class="dialog-actions"><button type="button" class="secondary" disabled={submitting} on:click={cancel}>Atšaukti</button><button type="submit" class="primary" disabled={submitting}>{submitting ? 'Ruošiama…' : 'Tęsti į Dokobit'}</button></div>
  </form>
</dialog>

<style>
  dialog{border:0;border-radius:8px;padding:0;max-width:520px;width:calc(100% - 32px);color:#333;box-shadow:0 16px 40px rgba(0,0,0,.25)}dialog::backdrop{background:rgba(0,0,0,.48)}form{position:relative;padding:22px;display:flex;flex-direction:column;gap:7px}.dialog-close{position:absolute;top:10px;right:10px;width:36px;height:36px;padding:0;border:0;border-radius:50%;background:transparent;color:#777;font-size:26px;font-weight:400;line-height:1;cursor:pointer}.dialog-close:hover{background:#eee;color:#555}.dialog-close:disabled{cursor:not-allowed;opacity:.5}h2{margin:0 40px 4px 0}p{margin:4px 0 10px;line-height:1.45}label{font-weight:600;margin-top:6px}input{min-height:44px;box-sizing:border-box;border:1px solid #bbb;border-radius:8px;background:#eeebdb;padding:10px;font-size:16px}input:focus,button:focus{outline:2px solid #14695b;outline-offset:2px}input[aria-invalid="true"]{border-color:#b91c1c}small,.error-summary{color:#b91c1c}.error-summary{background:#fee2e2;padding:10px;border-radius:8px}.privacy{font-size:14px;color:#666}.dialog-actions{display:flex;gap:10px;justify-content:flex-end;margin-top:10px}.dialog-actions button{min-height:44px;padding:9px 16px;border-radius:8px;font-weight:600}.primary{background:#fab45a;border:1px solid #333}.secondary{background:white;border:1px solid #14695b;color:#14695b}@media(max-width:767px){dialog{max-height:calc(100vh - 24px);overflow:auto}.dialog-actions{flex-direction:column-reverse}.dialog-actions button{width:100%}.dialog-close{width:36px}}
</style>
